import compose from 'lodash/fp/compose';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { PatientDataAccessAuthorizer } from '@dreryk/egabinet-patient-access-frontend-api';

import {
   createInterpolatedTranslationKey,
   createTranslatedText,
   TranslatableText,
   useTranslate,
} from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { prescriptionsHistoryDownloadTranslationKeys } from '../../translations';
import { PatientDataAccessFormController } from '../PersonDataAccessForm.types';

type PatientDataAccessAuthorizerSelectProps = {
   readonly register: PatientDataAccessFormController['register'];
   readonly patientAuthorizers: PatientDataAccessAuthorizer[];
};

const useGetAuthorizerLabel = (): ((authorizer?: PatientDataAccessAuthorizer) => TranslatableText) => {
   const translate = useTranslate();
   return useCallback(
      (authorizer) => {
         if (!authorizer) {
            return createTranslatedText('');
         }
         return createInterpolatedTranslationKey(
            prescriptionsHistoryDownloadTranslationKeys.patientAuthorizerSelectDisplayValue,
            {
               firstName: authorizer.firstName.asString(),
               lastName: authorizer.lastName.asString(),
               relation: translate(
                  prescriptionsHistoryDownloadTranslationKeys.patientAuthorizerRelation[authorizer.guardianRelation]
               ),
            }
         );
      },
      [translate]
   );
};

const StyledSelect = styled(Select)`
   max-width: 336px;
   flex: 1;
` as typeof Select;

const useAuthorizerDisplayableId = (): ((authorizer: PatientDataAccessAuthorizer) => string) => {
   return useCallback((authorizer: PatientDataAccessAuthorizer) => {
      if (authorizer.guardianRelation === 'NOT_APPLICABLE') {
         return 'currentPatient';
      }
      return authorizer.guardianId!.toString();
   }, []);
};

export const PatientDataAccessAuthorizerField = (props: PatientDataAccessAuthorizerSelectProps): JSX.Element => {
   const translate = useTranslate();
   const getAuthorizerLabel = useGetAuthorizerLabel();
   const triggerLabelExtractor = useMemo(() => compose(translate, getAuthorizerLabel), [translate, getAuthorizerLabel]);
   const getAuthorizerDisplayableId = useAuthorizerDisplayableId();
   return (
      <StyledSelect<PatientDataAccessAuthorizer>
         testId={'patientAuthorizers'}
         controller={props.register('authorizer', { required: true })}
         triggerLabelExtractor={triggerLabelExtractor}
         label={prescriptionsHistoryDownloadTranslationKeys.authorizingPerson}
         defaultValue={props.patientAuthorizers[0]}
      >
         <Select.List<PatientDataAccessAuthorizer>
            items={props.patientAuthorizers}
            keyExtractor={(authorizer) => getAuthorizerDisplayableId(authorizer)}
         >
            {(item) => (
               <Select.Item item={item} label={getAuthorizerLabel(item)} testId={getAuthorizerDisplayableId(item)} />
            )}
         </Select.List>
      </StyledSelect>
   );
};
